import { useEffect, useRef, useState } from 'react';
import {
  Card,
  Button,
  Input,
  Space,
  Typography,
  message,
  Form,
  Collapse,
  Spin,
  Alert,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';
import { isInDingTalk, getDingtalkAuthCode } from '../dingtalk';
import { resolveDingtalkCorpId } from '../lib/dingtalk-config';
import { tenantSlugFromHost } from '../lib/tenant-host';
import { useTenantBrand } from '../hooks/useTenantBrand';

export function LoginPage() {
  const hostTenantSlug = tenantSlugFromHost(window.location.hostname);
  useTenantBrand();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [loading, setLoading] = useState(false);
  const [autoLoginPhase, setAutoLoginPhase] = useState<
    'idle' | 'detecting' | 'in-progress' | 'failed' | 'fallback'
  >(isInDingTalk() ? 'detecting' : 'idle');
  const [autoLoginError, setAutoLoginError] = useState<string | null>(null);
  const autoTriedRef = useRef(false);
  const [form] = Form.useForm();
  const [devForm] = Form.useForm();

  const finishLogin = (accessToken: string, profile: any, hint?: string) => {
    setAuth(accessToken, profile);
    message.success(hint ?? `欢迎，${profile.name}`);
    navigate('/');
  };

  useEffect(() => {
    if (hostTenantSlug) {
      console.log('[Login] tenant from host', {
        slug: hostTenantSlug,
        hostname: window.location.hostname,
      });
    }
  }, [hostTenantSlug]);

  useEffect(() => {
    if (autoTriedRef.current) return;
    if (!isInDingTalk()) return;
    autoTriedRef.current = true;

    setAutoLoginPhase('in-progress');
    (async () => {
      try {
        const corpId = await resolveDingtalkCorpId();
        if (!corpId) {
          setAutoLoginPhase('failed');
          setAutoLoginError('当前公司未配置钉钉 CorpId，请联系管理员');
          return;
        }
        const code = await getDingtalkAuthCode(corpId);
        const r: any = await api.post('/auth/dingtalk/h5', { authCode: code });
        finishLogin(r.accessToken, r.profile, `欢迎，${r.profile.name}（钉钉免登）`);
      } catch (e: any) {
        setAutoLoginPhase('failed');
        setAutoLoginError(e?.message || '钉钉免登失败，请改用密码登录');
      }
    })();
  }, []);

  const onSimpleSubmit = async (vals: { tenantSlug?: string; name: string; password: string }) => {
    setLoading(true);
    try {
      const r: any = await api.post('/auth/simple-login', {
        tenantSlug: (vals.tenantSlug ?? hostTenantSlug ?? '').trim() || undefined,
        name: vals.name.trim(),
        password: vals.password,
      });
      finishLogin(r.accessToken, r.profile);
    } catch (e: any) {
      message.error(e?.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  const onDevEmpNo = async (vals: { empNo: string }) => {
    const tenantSlug = (form.getFieldValue('tenantSlug') ?? hostTenantSlug ?? '').trim();
    if (!tenantSlug) {
      message.warning('请先在上方填写公司标识，或使用租户专属域名访问');
      return;
    }
    setLoading(true);
    try {
      const r: any = await api.post('/auth/dev-login', {
        tenantSlug,
        empNo: vals.empNo.trim(),
      });
      finishLogin(r.accessToken, r.profile);
    } catch (e: any) {
      message.error(e?.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  const onDingtalkAuthCode = async (vals: { authCode: string }) => {
    setLoading(true);
    try {
      const r: any = await api.post('/auth/dingtalk/h5', { authCode: vals.authCode.trim() });
      finishLogin(r.accessToken, r.profile);
    } catch (e: any) {
      message.error(e?.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  if (autoLoginPhase === 'detecting' || autoLoginPhase === 'in-progress') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background:
            'linear-gradient(165deg, #e9e3ff 0%, #f3e8ff 42%, #ede9fe 100%)',
        }}
      >
        <Spin size="large" tip="钉钉免登中…" />
        <Typography.Text
          style={{ marginTop: 32, color: '#64748b', fontSize: 14 }}
        >
          检测到钉钉环境，正在为您自动登录
        </Typography.Text>
      </div>
    );
  }

  return (
    <div
      className="tg-login-root"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background:
          'linear-gradient(165deg, #e9e3ff 0%, #f3e8ff 42%, #ede9fe 100%)',
      }}
    >
      <Card
        className="tg-login-card"
        style={{
          width: 400,
          maxWidth: '100%',
          borderRadius: 16,
          boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12)',
          border: '1px solid rgba(255,255,255,0.8)',
        }}
        styles={{ body: { padding: '40px 36px 32px' } }}
      >
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Typography.Title
            level={2}
            style={{
              margin: 0,
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: 1,
              color: '#0f172a',
            }}
          >
            人才盘点系统
          </Typography.Title>
          <Typography.Text
            style={{
              display: 'block',
              marginTop: 14,
              color: '#64748b',
              fontSize: 15,
              lineHeight: 1.65,
            }}
          >
            对齐，再出发。
          </Typography.Text>
        </div>

        {autoLoginPhase === 'failed' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="钉钉免登失败"
            description={autoLoginError ?? '请改用下方密码登录或重新打开钉钉应用'}
            closable
            onClose={() => setAutoLoginPhase('fallback')}
          />
        )}

        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onSimpleSubmit}
            initialValues={{ tenantSlug: hostTenantSlug ?? undefined, password: '88888888' }}
            requiredMark={false}
          >
            {hostTenantSlug ? (
              <Form.Item label="公司">
                <Input value={hostTenantSlug} size="large" disabled style={{ borderRadius: 10 }} />
              </Form.Item>
            ) : (
              <Form.Item
                label="公司"
                name="tenantSlug"
                rules={[{ required: true, message: '请输入公司标识' }]}
              >
                <Input
                  placeholder="公司标识，如 demo"
                  size="large"
                  autoComplete="organization"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>
            )}
            <Form.Item label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
              <Input placeholder="与花名册一致" size="large" autoComplete="name" style={{ borderRadius: 10 }} />
            </Form.Item>
            <Form.Item label="口令" name="password" rules={[{ required: true, message: '请输入口令' }]}>
              <Input.Password
                placeholder="默认 88888888"
                size="large"
                autoComplete="current-password"
                style={{ borderRadius: 10 }}
              />
            </Form.Item>
            <Button
              block
              size="large"
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ height: 46, borderRadius: 10, fontWeight: 500, marginTop: 4 }}
            >
              登录
            </Button>
          </Form>

          {import.meta.env.DEV && (
            <Collapse
              ghost
              style={{ marginTop: 8 }}
              items={[
                {
                  key: 'adv',
                  label: <span style={{ color: '#94a3b8', fontSize: 13 }}>技术联调</span>,
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Form form={devForm} layout="vertical" onFinish={onDevEmpNo}>
                        <Form.Item name="empNo" label="工号（仅开发环境）">
                          <Input placeholder="ADMIN" />
                        </Form.Item>
                        <Button htmlType="submit" loading={loading} size="small">
                          工号登录
                        </Button>
                      </Form>
                      <Form layout="vertical" onFinish={onDingtalkAuthCode}>
                        <Form.Item
                          name="authCode"
                          label="钉钉 authCode"
                          rules={[{ required: true, message: '请输入' }]}
                        >
                          <Input placeholder="联调" />
                        </Form.Item>
                        <Button htmlType="submit" loading={loading} size="small">
                          换票
                        </Button>
                      </Form>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </Space>
      </Card>
    </div>
  );
}
